import { useEffect, useMemo, useRef } from 'react';

/**
 * Provides a stable callback reference that always invokes the latest version of the callback.
 *
 * Usage:
 *   const stableCallback = useCallbackRef((...args) => { ... });
 *   // stableCallback will always call the latest callback passed to the hook.
 *
 * This is useful when you need a memoized function that always has access to the most recent props/state.
 *
 * @template T extends (...args: never[]) => unknown
 * @param {T | undefined} callback - The callback function to be referenced.
 * @returns {T} A stable function reference that always calls the latest callback.
 *
 * Example:
 *   const handleClick = useCallbackRef((event) => { ... });
 *
 *   useEffect(() => {
 *     window.addEventListener('click', handleClick);
 *     return () => window.removeEventListener('click', handleClick);
 *   }, [handleClick]);
 *
 * The returned function identity is stable, but the implementation always reflects the latest callback.
 */
function useCallbackRef<T extends (...args: never[]) => unknown>(callback: T | undefined): T {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  return useMemo(() => {
    return ((...args) => {
      return callbackRef.current?.(...args);
    }) as T;
  }, []);
}

export { useCallbackRef };
